import { throwsNetworkError } from "@/utils/http/NetworkManagerMixin";
import { IBank, IBankAccount, IBanksClient } from "@/modules/banks/types";
import { EServices, IVForm } from "@/types";
import JackocoinsNetworkManagerMixin from "@/mixins/JackocoinsNetworkMangerMixin";

import { date, datetime } from "@/utils/time";
import {
  Component,
  Inject,
  Mixins,
  Prop,
  Ref,
  Watch,
} from "vue-property-decorator";
import { currencyImages, statusClassNames } from "../../constants";
import { IComment, ITransaction } from "../../types";
import VEmptyState from "@/vuetify-extensions/VEmptyState.vue";
import { namespace } from "vuex-class";
import TransactionsClient from "../../services/TransactionsClient";
import Comments from "../../components/Comments";

const BankModule = namespace("BankModule");

@Component({
  components: {
    VEmptyState,
    Comments,
  },
})
export default class TransactionDetails extends Mixins(
  JackocoinsNetworkManagerMixin
) {
  @Prop({
    type: Number,
    required: true,
  })
  id!: number;

  transactionDetail: ITransaction | null = null;
  bankLogo: any = null;
  showCommentsPanel = false;
  transactionComments: null | IComment[] = null;
  showReportDialog = false;
  commentText = "";

  @BankModule.State
  banks!: IBank[];

  commentList = [
    { text: "My Transaction Failed" },
    { text: "My Transaction Was Reversed" },
    { text: "The Money Exceeds My Bank Limit" },
    { text: "I Did Not Receive The Money" },
  ];

  @BankModule.State
  bankAccounts!: IBankAccount[];

  @BankModule.State
  newBankAccounts!: IBankAccount[];

  @Inject(EServices.transactions)
  transactionsClient!: TransactionsClient;

  @Inject(EServices.banks)
  banksClient!: IBanksClient;

  @Ref()
  copyPasteInput!: HTMLInputElement;

  @Ref()
  reportTransactionForm!: IVForm;

  datetime = datetime;

  get transaction(): ITransaction | null {
    return this.transactionDetail;
  }

  get paymentBank(): IBankAccount | boolean {
    return this.newBankAccounts.length < 1
      ? this.oldPaymentBank
      : this.newPaymentBank;
  }

  get oldPaymentBank(): IBankAccount {
    const account = this.bankAccounts.find(
      (account) => account.isDefault === true
    )!;
    try {
      this.bankLogo = require(`@/assets/images/${account.bank.name}.png`);
    } catch (e) {}
    return account;
  }

  get newPaymentBank(): IBankAccount {
    const account = this.newBankAccounts.find(
      (account) => account.isDefault === true
    )!;
    try {
      this.bankLogo = require(`@/assets/images/${account.bank.name}.png`);
    } catch (e) {}
    return account;
  }

  // get logs(): TransactionLog[] {
  //   return this.transactionInfo!.logs;
  // }

  get blockExplorerLink(): string {
    switch (this.transactionDetail!.currency.symbol.toUpperCase()) {
      case "BTC":
        return `https://blockchain.com/btc/tx/${
          this.transaction!.blockchainTransactionId
        }`;
      case "ETH":
        return `https://etherscan.io/tx/${
          this.transaction!.blockchainTransactionId
        }`;
      case "BNB":
        return `https://bscscan.com/tx/${
          this.transaction!.blockchainTransactionId
        }`;
      case "USDT":
        return `https://etherscan.io/tx/${
          this.transaction!.blockchainTransactionId
        }`;
      case "TRONUSDT":
        return `https://tronscan.org/#/transaction/${
          this.transaction!.blockchainTransactionId
        }`;
      default:
        throw "invalid currency type";
    }
  }

  getStatusClassName(status: string): string {
    return "lighten-5 " + statusClassNames[status];
  }

  getCurrencyImage(name: string): string {
    return require(`@/assets/images/icons/currencies/${name.toLowerCase()}.svg`);
  }

  copyTransactionId() {
    this.copyPasteInput.select();
    this.copyPasteInput.setSelectionRange(0, this.copyPasteInput.value.length);
    document.execCommand("copy");
    toast({ icon: "mdi-check", message: "ID Copied!", iconColor: "green" });
  }

  get comments() {
    return this.transactionComments;
  }

  get reportable() {
    // Check if this transaction has been approved, and also if the approved date is over 4 hours ago
    const timeApproved = this.transaction!.approveDate;

    if (!timeApproved) return false;
    if (this.transaction!.direction == "wallet") return false;

    const intervalBetweenTimeApprovedAndNow =
      new Date().getTime() - new Date(timeApproved).getTime();

    return (
      this.transaction!.status === "approved" &&
      intervalBetweenTimeApprovedAndNow > 40 * 60 * 1000
    );
  }

  @throwsNetworkError()
  async loadTransactionInfo() {
    const response = await this.transactionsClient.fetchTransaction(this.id);
    this.transactionDetail = response.data;
  }

  @throwsNetworkError()
  async loadOldBankDetails() {
    await this.banksClient.getBankAccounts();
  }

  @throwsNetworkError()
  async loadNewBankDetails() {
    await this.banksClient.getNewBankAccounts();
  }

  @throwsNetworkError()
  async callTransactionComments() {
    const response = await this.transactionsClient.getTransactionComments(
      this.transaction!.transactionId
    );
    this.transactionComments = response;
  }

  @throwsNetworkError()
  async callReportTransaction() {
    if (!this.reportTransactionForm.validate()) return;
    this.loading.callReportTransaction = true;
    this.transactionDetail = await this.transactionsClient.reportTransaction({
      transactionId: this.transaction!.transactionId,
      message: this.commentText,
    });
    toast({
      icon: "mdi-check",
      iconColor: "green",
      message: "Transaction reported",
    });
    this.showReportDialog = false;
    this.loading.callReportTransaction = false;
  }

  @Watch("error.loadNewBankDetails")
  onLoadBankDetailsError(value: string) {
    if (value)
      toast({
        icon: "mdi-exlamation-think",
        iconColor: "red",
        message: this.error.loadNewBankDetails,
      });
  }

  @Watch("error.loadTransactionInfo")
  onLoadTransactionInfoError(value: string) {
    if (value)
      toast({
        icon: "mdi-exlamation-think",
        iconColor: "red",
        message: this.error.loadTransactionInfo,
      });
  }

  @Watch("error.callTransactionComments")
  onLoadCallTransactionCommentsError(value: string) {
    if (value)
      toast({
        icon: "mdi-exlamation-think",
        iconColor: "red",
        message: this.error.callTransactionComments,
      });
  }

  @Watch("error.callReportTransaction")
  onLoadCallReportTransactionError(value: string) {
    if (value)
      toast({
        icon: "mdi-exlamation-think",
        iconColor: "red",
        message: this.error.callReportTransaction,
      });
  }

  mounted() {
    this.loadTransactionInfo().then(() => this.callTransactionComments());
    this.loadNewBankDetails();
    this.loadOldBankDetails();
  }
}
